import { useTranslation } from 'react-i18next'
import type { ActionModuleEmployeeResponse, ActionModuleInstanceIncentivationRequest, OrderType } from '@/api/model'
import { AccountStatus } from '@/api/model'
import { IncentivationDataType } from '@/api/model/incentivationDataType'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/shadcn/table'
import { SortableTableHead } from '@/components/common/SortableTableHead'
import { LoaderTable } from '@/components/common/loaders/LoaderTable'
import { cn } from '@/lib/utils'
import { InstanceStatusIcon } from '@/pages/action-modules/components/InstanceStatusIcon'
import { fmtDate } from '@/pages/incentivation/lib'
import { ScoreCell } from './ScoreCell'
import { ScoreEditCell, type RewardCalculationInformation } from './ScoreEditCell'

interface Props {
  employees: ActionModuleEmployeeResponse[]
  isLoading: boolean
  isError: boolean
  sort?: string
  order?: OrderType
  onSort: (sort: string | undefined, order: OrderType | undefined) => void
  selectedEmployeeId: string | null
  onSelectEmployee: (employee: ActionModuleEmployeeResponse) => void
  /** When another column is open the table shrinks to first name, last name and status. */
  compact: boolean
  /** When true the Score cell of editable rows becomes an input (drill-down stays enabled). */
  editMode: boolean
  /** Per-module input type for the editable Score cell. */
  dataType: IncentivationDataType
  /** Draft PATCH request per instance id (survives pagination/sort). */
  drafts: Map<number, ActionModuleInstanceIncentivationRequest>
  /** Reports a dirty change (info) or a revert-to-original (null) up to the tab. */
  onDraftChange: (employeeUserId: string, instanceId: number, info: RewardCalculationInformation | null) => void
}

/**
 * A row is editable only when it has an instance to patch and its employee is active.
 * Rows without a `lastInstanceId` (nothing to PATCH) or blocked employees stay read-only.
 */
function isEditable(emp: ActionModuleEmployeeResponse): boolean {
  return emp.lastInstanceId !== null && emp.status === AccountStatus.Active
}

/**
 * Panel 1 of the Miller-column unit: the employee table. Score = latest instance's
 * incentivation data, Points = aggregated `totalPoints`. Selecting a row lifts the
 * employee up and shrinks the panel to first name, last name and status. Presentational —
 * the query, pagination and footer live on the parent (IncentivationTab) so the footer can
 * span the whole unit.
 */
export function EmployeeColumn({
  employees, isLoading, isError, sort, order, onSort, selectedEmployeeId, onSelectEmployee, compact,
  editMode, dataType, drafts, onDraftChange,
}: Props) {
  const { t } = useTranslation('action-modules-detail')
  // Non-compact: First name, Last name, Position, EmployeeNumber, Status, Last action,
  // Score, Points = 8 columns. Compact keeps First name, Last name and Status.
  const colCount = compact ? 3 : 8

  return (
    <div className="flex min-h-full flex-col">
      {/* No own scroll area: the panel row on the tab scrolls all columns together. */}
      <div className="flex-1">
      <Table>
        <TableHeader className="bg-card shadow-sm [&_th]:bg-card">
          <TableRow>
            <SortableTableHead field="FirstName" currentSort={sort} currentOrder={order} onSort={onSort}>
              {t('incentivation.columns.firstName')}
            </SortableTableHead>
            <SortableTableHead field="LastName" currentSort={sort} currentOrder={order} onSort={onSort}>
              {t('incentivation.columns.lastName')}
            </SortableTableHead>
            {!compact && (
              <SortableTableHead field="Position" currentSort={sort} currentOrder={order} onSort={onSort}>
                {t('incentivation.columns.position')}
              </SortableTableHead>
            )}
            {!compact && (
              <SortableTableHead field="EmployeeNumber" currentSort={sort} currentOrder={order} onSort={onSort}>
                {t('incentivation.columns.employeeNumber')}
              </SortableTableHead>
            )}
            <TableHead>{t('incentivation.columns.status')}</TableHead>
            {!compact && (
              <SortableTableHead field="LastActionDate" currentSort={sort} currentOrder={order} onSort={onSort}>
                {t('incentivation.columns.lastActionDate')}
              </SortableTableHead>
            )}
            {!compact && <TableHead>{t('incentivation.columns.score')}</TableHead>}
            {!compact && <TableHead className="text-right">{t('incentivation.columns.points')}</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          <LoaderTable
            isLoading={isLoading}
            isError={isError}
            isEmpty={employees.length === 0}
            colSpan={colCount}
            errorMessage={t('incentivation.noEmployees')}
            emptyMessage={t('incentivation.noEmployees')}
          >
            {employees.map((emp) => {
              const id = emp.id ?? ''
              const isSelected = id === selectedEmployeeId
              const editable = editMode && isEditable(emp)
              return (
                <TableRow
                  key={id}
                  // Drill-down stays enabled while editing (row clicks still select).
                  className={cn('cursor-pointer', isSelected && 'bg-muted/40')}
                  onClick={() => onSelectEmployee(emp)}
                >
                  <TableCell className="font-medium">{emp.firstName ?? '—'}</TableCell>
                  <TableCell className="font-medium">{emp.lastName ?? '—'}</TableCell>
                  {!compact && <TableCell className="text-muted-foreground">{emp.position ?? '—'}</TableCell>}
                  {!compact && <TableCell className="text-muted-foreground">{emp.employeeNumber ?? '—'}</TableCell>}
                  <TableCell><InstanceStatusIcon status={emp.lastInstanceStatus} /></TableCell>
                  {!compact && <TableCell className="text-muted-foreground">{fmtDate(emp.lastInstanceCompletedAt)}</TableCell>}
                  {!compact && (
                    // Editing the score must not bubble up to the row's drill-down click.
                    <TableCell onClick={editable ? (e) => e.stopPropagation() : undefined}>
                      {editable
                        ? <ScoreEditCell
                            dataType={dataType}
                            data={emp.incentivationData}
                            draft={drafts.get(emp.lastInstanceId as number)?.rewardCalculationInformation ?? undefined}
                            onChange={(info) => onDraftChange(emp.id ?? '', emp.lastInstanceId as number, info)}
                          />
                        : <ScoreCell data={emp.incentivationData} />}
                    </TableCell>
                  )}
                  {!compact && <TableCell className="text-right font-semibold">{emp.totalPoints}</TableCell>}
                </TableRow>
              )
            })}
          </LoaderTable>
        </TableBody>
      </Table>
      </div>
    </div>
  )
}
